import {
  asQueryString,
  AuthorizationFetcher,
  BasicEnumDefinition,
  ConfigObjectType,
  DlMuiButton,
  FetcherUtils,
  PopupWrapper,
  useApiQuery,
  useDefaultScrollTo,
  useLocalStorage,
} from '@wallaby-ui/core';
import {NewMinimizedMaximized} from '@wallaby-ui/core/src/js/components/LightPageLayout';
import {
  FreeSoloValue,
  LightSelectFilter,
  LightPanelGroup,
  MerieSchema,
  RISK_FACTOR_LABEL_COLORS,
  hospitalizationsContainer,
  isFreeSolo,
  useFormatters,
  usePermissions,
} from '@wallaby-ui/document';
import {sortBy} from 'lodash';
import React, {useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {HospitalizationState} from './HospitalizationDetailLayoutV2';
import {DEFAULT_HOSPITALIZATION_TIMELINE_FILTERS} from './HospitalizationDetailTimelineV2';
import {useAppSelector} from 'src/js/store_hooks';

type HospitalizationDetailRiskFactorsProps = {
  state: HospitalizationState;
  changeState: (diff: Partial<HospitalizationState>) => void;
  storageIdPrefix: string;
  config: ConfigObjectType;
  hospitalizationId: string;
  riskFactors: MerieSchema.components['schemas']['RiskFactorDto'][];
  riskFactorsEtag: string | undefined;
  oldRiskFactorIds: Set<number>;
  reloadRiskFactors: () => void;
  setTimelineDefaultScroll: (v: number) => void;
  setMinimizedMaximized: (newValues: NewMinimizedMaximized) => void;
};
export const HospitalizationRiskFactorsV2: React.FC<HospitalizationDetailRiskFactorsProps> = (props) => {
  const {
    state,
    changeState,
    storageIdPrefix,
    config,
    hospitalizationId,
    riskFactors,
    riskFactorsEtag: _riskFactorsEtag,
    oldRiskFactorIds,
    reloadRiskFactors,
    setTimelineDefaultScroll,
    setMinimizedMaximized,
  } = props;
  const {userRoles} = usePermissions(config);
  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.riskFactorsScroll`, 0);
  const scrollRef = useDefaultScrollTo(defaultScroll);
  const onScroll = (event: React.UIEvent<HTMLElement>) => setDefaultScroll((event.target as HTMLElement).scrollTop);
  const {t, tEnum} = useFormatters();
  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  // data
  const riskFactorsWithSortedLabels = useMemo(
    () =>
      riskFactors.map((riskFactor) => {
        const sortedLabels = riskFactor.labels.sort(
          (labelA, labelB) => (RISK_FACTOR_LABEL_COLORS[labelA]?.order || 0) - (RISK_FACTOR_LABEL_COLORS[labelB]?.order || 0)
        );

        return {
          ...riskFactor,
          labels: sortedLabels,
        };
      }),
    [riskFactors]
  );
  const activeRiskFactors = useMemo(() => riskFactorsWithSortedLabels.filter((v) => v.isActive), [riskFactorsWithSortedLabels]);
  const deactivatedRiskFactors = useMemo(() => riskFactorsWithSortedLabels.filter((v) => !v.isActive), [riskFactorsWithSortedLabels]);
  const enumMap = useAppSelector((state) => state.enum.enumMap);
  const riskFactorTypesForAddUrl = config.service.url.hospitalizationRiskFactors.replace('{evaluatedEncounterId}', hospitalizationId);
  const {
    data: riskFactorTypesForAdd,
    isLoading: riskFactorTypesForAddIsLoading,
    isError: _isError,
    refetch: refetchRiskFactorTypesForAdd,
  } = useApiQuery<string[]>([riskFactorTypesForAddUrl], null, {
    axiosConfig: {
      url: riskFactorTypesForAddUrl,
    },
    reactQueryOptions: {
      enabled: false, // load only on refetch
    },
  });
  const containsNoDetailInfo = (riskFactor: MerieSchema.components['schemas']['RiskFactorDto']) => {
    return !Object.keys(riskFactor).some((key) => key.endsWith('Detail')) && riskFactor.narrowTherapeuticIndexMedication == null;
  };
  const findRiskFactor = useCallback(
    (wanted: string | FreeSoloValue, isActive: boolean = true) => {
      return isFreeSolo(wanted)
        ? riskFactors.find((rf) => rf.type === 'OTHER' && rf.typeOtherUserGivenName === wanted.freeSolo && rf.isActive === isActive)
        : riskFactors.find((rf) => rf.type === wanted && rf.isActive === isActive && containsNoDetailInfo(rf));
    },
    [riskFactors]
  );
  const reloadRiskFactorsOnDelete = useCallback(
    (deletedId: string) => {
      reloadRiskFactors();
      const newRiskFactorsFilter = (state.timeline?.riskFactors ?? []).filter((v) => v !== deletedId);
      if (newRiskFactorsFilter.length !== (state.timeline?.riskFactors ?? []).length) {
        changeState({
          timeline: {
            ...state.timeline,
            riskFactors: newRiskFactorsFilter.length ? newRiskFactorsFilter : undefined,
          },
        });
        setTimelineDefaultScroll(0);
      }
    },
    [changeState, reloadRiskFactors, setTimelineDefaultScroll, state.timeline]
  );
  const addRiskFactorTypeValues = useMemo(() => {
    const notAddedRiskFactorTypes: string[] = riskFactorTypesForAdd ?? [];
    const disabledRiskFactorTypes: string[] = riskFactors.filter((v) => !v.isActive && containsNoDetailInfo(v)).map((v) => v.type);
    return (enumMap.RiskFactorTypeEnum ?? []).filter((v: BasicEnumDefinition) => {
      if (v.name === 'OTHER') return false; // NOTE: `'OTHER'` is handled by `freeSolo`
      return notAddedRiskFactorTypes.includes(v.name) || disabledRiskFactorTypes.includes(v.name);
    });
  }, [enumMap, riskFactorTypesForAdd, riskFactors]);

  type RiskFactorTypeEnum = MerieSchema.components['schemas']['RiskFactorTypeEnum'];

  const activateOrDeactivateRiskFactor = React.useCallback(
    (riskFactorId: string | number, isDeactivated: boolean) => {
      let url = config.service.url.hospitalizationRiskFactorToggleActivated;
      url = url.replace('{id}', hospitalizationId);
      url = url.replace('{riskFactorId}', riskFactorId);
      return AuthorizationFetcher.interceptedFetch(
        url,
        {
          method: 'PATCH', // NOTE: BE returns 400 if 'patch' is lowercase
          body: JSON.stringify({
            isActive: isDeactivated,
          }),
        },
        {
          'Content-Type': 'application/json',
          // 'If-Match': riskFactorsEtag,
        }
      )
        .then((response) => {
          FetcherUtils.processEmptyContentResponse(response);
          reloadRiskFactorsOnDelete(String(riskFactorId));
        })
        .catch((response) => {
          FetcherUtils.errorHandler(response);
        });
    },
    [hospitalizationId, config, reloadRiskFactorsOnDelete]
  );

  type RiskFactorCreateDto = MerieSchema.components['schemas']['RiskFactorCreateDto'];
  const addRiskFactorUrl = `${config.service.url.hospitalizationRiskFactor}/${hospitalizationId}/risk-factors`;
  const addRiskFactor = React.useCallback(
    (data: RiskFactorCreateDto) => {
      return AuthorizationFetcher.interceptedFetch(
        addRiskFactorUrl,
        {
          method: 'post',
          body: JSON.stringify(data),
        },
        {
          'Content-Type': 'application/json',
        }
      )
        .then((response) => {
          FetcherUtils.processEmptyContentResponse(response);
          reloadRiskFactors();
        })
        .catch((response) => {
          FetcherUtils.errorHandler(response);
        });
    },
    [addRiskFactorUrl, reloadRiskFactors]
  );

  const addOrActivateRiskFactor = React.useCallback(
    (type: RiskFactorTypeEnum | FreeSoloValue) => {
      const isInactiveRiskFactor = findRiskFactor(type, false);
      if (isInactiveRiskFactor) {
        return activateOrDeactivateRiskFactor(isInactiveRiskFactor.id, true);
      }
      return addRiskFactor(isFreeSolo(type) ? {type: 'OTHER', typeOtherUserGivenName: type.freeSolo} : {type});
    },
    [findRiskFactor, activateOrDeactivateRiskFactor, addRiskFactor]
  );
  // components
  const [addRiskFactorAnchorTag, setAddRiskFactorAnchorTag] = useState(null as HTMLElement | null);
  const [addRiskFactorType, setAddRiskFactorType] = useState(null as RiskFactorTypeEnum | FreeSoloValue | null);
  const addButton = (
    <PopupWrapper
      anchorTag={addRiskFactorAnchorTag}
      openButton={
        <DlMuiButton
          type='chip-filled'
          title='riskFactors.add'
          onClick={(event) => {
            const element = event.nativeEvent.target as HTMLElement;
            (document.activeElement as HTMLElement | null)?.blur();
            setAddRiskFactorAnchorTag(element);
            refetchRiskFactorTypesForAdd();
          }}
          disabled={!userRoles.ENCOUNTER_DETAIL_EDIT_ROLE}
        />
      }
      showDiscardSaveButtons
      onClose={() => {
        setAddRiskFactorAnchorTag(null);
        setAddRiskFactorType(null);
      }}
      canConfirm={addRiskFactorType !== null}
      onConfirm={() => {
        if (!addRiskFactorType) return;
        addOrActivateRiskFactor(addRiskFactorType);
        setAddRiskFactorAnchorTag(null);
        setAddRiskFactorType(null);
      }}
      contentStyle={{minWidth: 400}}
    >
      <LightSelectFilter
        config={{
          name: 'addRiskFactorType',
          ui: {
            label: 'riskFactors.add.type',
          },
        }}
        loading={riskFactorTypesForAddIsLoading}
        options={sortByMessageKey(addRiskFactorTypeValues)}
        freeSolo
        freeSoloFormat={`${tEnum('RiskFactorTypeEnum', 'OTHER')} - {}`}
        getFreeSoloDisabled={(option) => findRiskFactor(option, true) !== undefined}
        value={addRiskFactorType}
        onChange={(newValue: RiskFactorTypeEnum | FreeSoloValue) => {
          setAddRiskFactorType(newValue);
        }}
        isClearable={false}
      />
    </PopupWrapper>
  );
  const ADD_BUTTON_HEIGHT = 26;
  // changeRiskFactorFilter // REFACTOR: just use a link to navigate?
  const navigate = useNavigate();
  const changeRiskFactorFilter = useCallback(
    (newRiskFactorId: number) => {
      setTimelineDefaultScroll(0);
      setMinimizedMaximized({maximized: false, expanded: false});
      navigate(
        `/hospitalizedPatients/v2/${hospitalizationId}?${asQueryString({
          timeline: {
            ...DEFAULT_HOSPITALIZATION_TIMELINE_FILTERS, // NOTE: we clear other timeline filters
            riskFactors: [String(newRiskFactorId)],
          },
        })}`
      );
    },
    [setTimelineDefaultScroll, navigate, hospitalizationId, setMinimizedMaximized]
  );
  return (
    <>
      <div style={{display: 'flex', justifyContent: 'flex-end'}}>{addButton}</div>
      <div
        data-fill-available
        style={{height: `calc(100% - var(--horizontal-tab-height) + ${ADD_BUTTON_HEIGHT}px)`}} // NOTE: browsers don't support `max-height: fill-available;` yet, so we calculate it ourselves
        ref={scrollRef}
        onScroll={onScroll}
      >
        {/* The Alerts panel's groups (PDM-2758): Active and Deactivated, both open by default, the counts of their factors */}
        <LightPanelGroup
          iconName='rfGroupActiveIcon'
          viewBox='0 0 20 20'
          label={t('riskFactors.title.active')}
          count={activeRiskFactors.length}
          storageKey={`${storageIdPrefix}.riskFactors.group.ACTIVE`}
          defaultOpen
          first
          dataName={'dl-risk-factor-group'}
          groupId='ACTIVE'
        >
          <hospitalizationsContainer.RiskFactorPanelRows
            config={config}
            isDeactivated={false}
            riskFactors={activeRiskFactors}
            oldRiskFactorIds={oldRiskFactorIds}
            changeRiskFactorFilter={changeRiskFactorFilter}
            activateOrDeactivateRiskFactor={activateOrDeactivateRiskFactor}
          />
        </LightPanelGroup>
        <LightPanelGroup
          iconName='rfGroupDeactivatedIcon'
          viewBox='0 0 20 20'
          label={t('riskFactors.group.deactivated')}
          count={deactivatedRiskFactors.length}
          storageKey={`${storageIdPrefix}.riskFactors.group.DEACTIVATED`}
          defaultOpen
          dataName={'dl-risk-factor-group'}
          groupId='DEACTIVATED'
        >
          <hospitalizationsContainer.RiskFactorPanelRows
            config={config}
            isDeactivated
            riskFactors={deactivatedRiskFactors}
            oldRiskFactorIds={oldRiskFactorIds}
            changeRiskFactorFilter={changeRiskFactorFilter}
            activateOrDeactivateRiskFactor={activateOrDeactivateRiskFactor}
          />
        </LightPanelGroup>
      </div>
    </>
  );
};
